import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  CheckCircle2,
  Shield,
  Sparkles,
  X,
} from "lucide-react";
import P2PCreateOutcomeStep from "./P2PCreateOutcomeStep";
import P2PCreateStakesStep from "./P2PCreateStakesStep";
import { STAKE_STEP_META } from "./p2pCreateConstants";
import { formatFixtureDate, formatFixtureTime } from "../../../utils/formatFixtureDate";
import {
  canShowP2PPostOfferOnFixture,
  isFixtureLive,
} from "../../../utils/p2pCreateHelpers";
import { labelForP2PField } from "../../../data/p2pFieldLabels";
import { selectAppUser } from "../../../redux/appAuthSlice";

const newIdempotencySuffix = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;

const PHASES = [
  { id: "outcome", label: "Pick" },
  { id: "amount", label: "Budget" },
  { id: "invites", label: "Friends" },
  { id: "codes", label: "Codes" },
  { id: "slots", label: "Launch" },
];

const resetForm = () => ({
  selected: null,
  stakeStep: 1,
  amount: "",
  minStake: "500",
  hasInvite: null,
  inviteCodes: [""],
  maxPeople: "1",
  error: "",
});

const P2PCreateModal = ({ open, fixture, league, onClose, onSubmit }) => {
  const user = useSelector(selectAppUser);
  const createIdempotencyKeyRef = useRef(null);
  const submitLockRef = useRef(false);
  const [phase, setPhase] = useState("outcome");
  const [stakeStep, setStakeStep] = useState(1);
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [offeredOdds, setOfferedOdds] = useState("2");
  const [minStake, setMinStake] = useState("500");
  const [stance, setStance] = useState("for");
  const [hasInvite, setHasInvite] = useState(null);
  const [inviteCodes, setInviteCodes] = useState([""]);
  const [maxPeople, setMaxPeople] = useState("1");
  const [error, setError] = useState("");

  const live = fixture ? isFixtureLive(fixture) : false;

  const selectedLabel = useMemo(() => {
    if (!selected) return null;
    return [
      labelForP2PField(selected.section),
      selected.subSection ? labelForP2PField(selected.subSection) : null,
      labelForP2PField(selected.key) || selected.key,
    ]
      .filter(Boolean)
      .join(", ");
  }, [selected]);

  const activePhaseIndex = useMemo(() => {
    if (phase === "outcome") return 0;
    if (stakeStep === 1) return 1;
    if (stakeStep === 2) return 2;
    if (stakeStep === 3) return 3;
    return 4;
  }, [phase, stakeStep]);

  const visiblePhases = useMemo(() => {
    if (hasInvite === false) return PHASES.filter((p) => p.id !== "codes");
    return PHASES;
  }, [hasInvite]);

  const handleClose = useCallback(() => {
    const next = resetForm();
    createIdempotencyKeyRef.current = null;
    setPhase("outcome");
    setStakeStep(next.stakeStep);
    setSelected(next.selected);
    setAmount(next.amount);
    setOfferedOdds("2");
    setMinStake(next.minStake);
    setStance("for");
    setHasInvite(next.hasInvite);
    setInviteCodes(next.inviteCodes);
    setMaxPeople(next.maxPeople);
    setError(next.error);
    submitLockRef.current = false;
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, handleClose]);

  useEffect(() => {
    if (open) {
      const next = resetForm();
      createIdempotencyKeyRef.current = null;
      setPhase("outcome");
      setStakeStep(next.stakeStep);
      setSelected(next.selected);
      setAmount(next.amount);
      setOfferedOdds("2");
      setMinStake(next.minStake);
      setStance("for");
      setHasInvite(next.hasInvite);
      setInviteCodes(next.inviteCodes);
      setMaxPeople(next.maxPeople);
      setError(next.error);
      submitLockRef.current = false;
    }
  }, [open, fixture?._id]);

  if (!open || !fixture) return null;

  const validateStakeStep = () => {
    const amountNum = parseFloat(amount);
    const oddsNum = parseFloat(offeredOdds);
    const minStakeNum = parseFloat(minStake);
    const invitedCount = hasInvite
      ? inviteCodes.map((c) => c.trim()).filter(Boolean).length
      : 0;
    const publicSlots = parseInt(maxPeople || "0", 10) || 0;

    switch (stakeStep) {
      case 1:
        if (!amount.trim() || Number.isNaN(amountNum) || amountNum < 500) {
          return "Minimum creator budget is Cash 500.";
        }
        if (Number.isNaN(oddsNum) || oddsNum < 1.01 || oddsNum > 50) {
          return "Enter valid offered odds (1.01–50).";
        }
        {
          const capacity =
            oddsNum > 1 ? Math.round((amountNum / (oddsNum - 1)) * 100) / 100 : 0;
          if (capacity < 500) {
            const needed = Math.ceil(500 * (oddsNum - 1));
            return `At these odds your budget cannot cover a Cash 500 min stake. Raise budget to at least Cash ${needed} or lower odds.`;
          }
          if (Number.isNaN(minStakeNum) || minStakeNum < 500) {
            return "Minimum counter stake is Cash 500.";
          }
          if (minStakeNum > capacity) {
            const seatCost = Math.round(minStakeNum * (oddsNum - 1) * 100) / 100;
            return `Min stake Cash ${minStakeNum} would cost Cash ${seatCost} if that counter wins, but budget is only Cash ${amountNum}. Max affordable min stake: Cash ${capacity}.`;
          }
        }
        return "";
      case 2:
        if (hasInvite === null) return "Choose whether you're inviting friends.";
        return "";
      case 3:
        if (!inviteCodes.map((c) => c.trim()).filter(Boolean).length) {
          return "Add at least one friend code.";
        }
        return "";
      case 4:
        if (hasInvite && !inviteCodes.map((c) => c.trim()).filter(Boolean).length) {
          return "Add at least one friend code.";
        }
        return "";
      default:
        return "";
    }
  };

  const submitCreate = () => {
    if (submitLockRef.current) return;

    const userId = user?._id || user?.id;
    const fixtureId = fixture?._id || fixture?.id;

    if (!userId || !fixtureId || !selected) {
      setError("Missing user or match data.");
      return;
    }

    if (!canShowP2PPostOfferOnFixture(fixture)) {
      setError(
        "P2P creation is closed for this match — it has started, finished, or is within 10 minutes of kickoff."
      );
      return;
    }

    setError("");
    submitLockRef.current = true;

    if (!createIdempotencyKeyRef.current) {
      createIdempotencyKeyRef.current = `p2p:create:${userId}:${fixtureId}:${newIdempotencySuffix()}`;
    }

    const inviteList = hasInvite
      ? inviteCodes.map((c) => c.trim()).filter(Boolean)
      : [];
    let joinMode = "public";
    if (hasInvite && (parseInt(maxPeople || "0", 10) || 0) > 0) {
      joinMode = "public_and_invite";
    } else if (hasInvite) {
      joinMode = "invite_only";
    }

    const payload = {
      userId,
      fixtureId,
      agreedOdds: selected,
      stance,
      offeredOdds: parseFloat(offeredOdds),
      creatorBudget: parseFloat(amount),
      minStake: parseFloat(minStake),
      joinMode,
      inviteCodes: inviteList,
      idempotencyKey: createIdempotencyKeyRef.current,
    };

    const submit = onSubmit;
    handleClose();
    submit?.(payload);
  };

  const goNext = () => {
    setError("");
    if (phase === "outcome") {
      if (!selected) {
        setError("Select an outcome to continue.");
        return;
      }
      setPhase("stakes");
      setStakeStep(1);
      return;
    }

    const err = validateStakeStep();
    if (err) {
      setError(err);
      return;
    }

    if (stakeStep === 2 && hasInvite === false) {
      setStakeStep(4);
      return;
    }
    if (stakeStep < 4) {
      setStakeStep((s) => s + 1);
      return;
    }

    submitCreate();
  };

  const goBack = () => {
    setError("");
    if (phase === "outcome") {
      handleClose();
      return;
    }
    if (stakeStep === 1) {
      setPhase("outcome");
      return;
    }
    if (stakeStep === 4 && hasInvite === false) {
      setStakeStep(2);
      return;
    }
    setStakeStep((s) => s - 1);
  };

  const isLastStep = phase === "stakes" && stakeStep === 4;
  const primaryLabel = isLastStep ? "Submit" : "Continue";

  const stakeTitle = phase === "stakes" ? STAKE_STEP_META[stakeStep - 1]?.title : null;

  return createPortal(
    <div className="pcm2-root" role="dialog" aria-modal="true" aria-labelledby="pcm2-title">
      <button type="button" className="pcm2-backdrop" aria-label="Close" onClick={handleClose} />

      <div className="pcm2-sheet">
        {/* Hero header */}
        <header className="pcm2-hero">
          <div className="pcm2-hero-top">
            <div className="pcm2-hero-copy">
              <p className="pcm2-hero-eyebrow">
                <Sparkles size={12} aria-hidden />
                Create P2P challenge
              </p>
              <h2 id="pcm2-title" className="pcm2-hero-title">
                {phase === "outcome" ? "What's your call?" : stakeTitle}
              </h2>
            </div>
            <button type="button" className="pcm2-close" onClick={handleClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>

          <div className="pcm2-fixture">
            <div className="pcm2-fixture-team">
              <div className="pcm2-fixture-crest">
                {fixture.homeTeam?.logo ? (
                  <img src={fixture.homeTeam.logo} alt="" />
                ) : (
                  <Shield size={20} />
                )}
              </div>
              <span>{fixture.homeTeam?.name || "Home"}</span>
            </div>
            <div className="pcm2-fixture-mid">
              <span className={`pcm2-fixture-live${live ? " pcm2-fixture-live-on" : ""}`}>
                {live ? "LIVE" : formatFixtureTime(fixture.date)}
              </span>
              <span className="pcm2-fixture-vs">vs</span>
              {league?.name ? <span className="pcm2-fixture-league">{league.name}</span> : null}
            </div>
            <div className="pcm2-fixture-team pcm2-fixture-team-away">
              <div className="pcm2-fixture-crest">
                {fixture.awayTeam?.logo ? (
                  <img src={fixture.awayTeam.logo} alt="" />
                ) : (
                  <Shield size={20} />
                )}
              </div>
              <span>{fixture.awayTeam?.name || "Away"}</span>
            </div>
          </div>
          <p className="pcm2-fixture-date">
            {live ? "In progress" : formatFixtureDate(fixture.date)}
          </p>
        </header>

        {/* Progress rail */}
        <nav className="pcm2-progress" aria-label="Create progress">
          {visiblePhases.map((p, i) => {
            const idx = PHASES.findIndex((x) => x.id === p.id);
            const done = idx < activePhaseIndex;
            const current = idx === activePhaseIndex;
            return (
              <div
                key={p.id}
                className={`pcm2-progress-item${done ? " pcm2-progress-done" : ""}${
                  current ? " pcm2-progress-current" : ""
                }`}
              >
                <span className="pcm2-progress-dot">
                  {done ? <CheckCircle2 size={12} /> : i + 1}
                </span>
                <span className="pcm2-progress-label">{p.label}</span>
              </div>
            );
          })}
        </nav>

        {/* Body */}
        <div className="pcm2-body">
          {error ? <p className="pcm2-error">{error}</p> : null}

          {phase === "outcome" ? (
            <P2PCreateOutcomeStep
              fixture={fixture}
              selected={selected}
              onSelect={setSelected}
              stance={stance}
              onStanceChange={setStance}
            />
          ) : (
            <P2PCreateStakesStep
              step={stakeStep}
              amount={amount}
              onAmountChange={setAmount}
              offeredOdds={offeredOdds}
              onOfferedOddsChange={setOfferedOdds}
              minStake={minStake}
              onMinStakeChange={setMinStake}
              hasInvite={hasInvite}
              onHasInviteChange={setHasInvite}
              inviteCodes={inviteCodes}
              onInviteCodesChange={setInviteCodes}
              maxPeople={maxPeople}
              onMaxPeopleChange={setMaxPeople}
            />
          )}
        </div>

        {/* Sticky footer */}
        <footer className="pcm2-foot">
          {selectedLabel ? (
            <div className="pcm2-foot-pick">
              <CheckCircle2 size={14} aria-hidden />
              <span>{selectedLabel}</span>
            </div>
          ) : (
            <p className="pcm2-foot-hint">
              {phase === "outcome"
                ? "Tap a market outcome above"
                : "Fill in the details to launch your challenge"}
            </p>
          )}
          <div className="pcm2-foot-actions">
            <button type="button" className="pcm2-btn pcm2-btn-ghost" onClick={goBack}>
              {phase === "outcome" ? "Cancel" : "Back"}
            </button>
            <button
              type="button"
              className="pcm2-btn pcm2-btn-primary"
              onClick={goNext}
              disabled={phase === "outcome" && !selected}
            >
              {primaryLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default P2PCreateModal;
